import { useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

export const isSeriesTask = (task) =>
  !!(task?.complianceObligation || task?.metadata?.programmeObligationId || task?.metadata?.programme_obligation_id || task?.recurrenceType === 'repeating' || task?.parentTaskId)

export const useSeriesScope = (task) => {
  const [open, setOpen] = useState(false)
  const resolverRef = useRef(null)

  const requestScope = useCallback(() => {
    if (!isSeriesTask(task)) return Promise.resolve('this')
    return new Promise((resolve) => {
      resolverRef.current = resolve
      setOpen(true)
    })
  }, [task])

  const choose = (scope) => {
    setOpen(false)
    const resolve = resolverRef.current
    resolverRef.current = null
    resolve?.(scope)
  }

  const isObligation = !!(task?.complianceObligation || task?.metadata?.programmeObligationId || task?.metadata?.programme_obligation_id)
  const obligationName = task?.complianceObligation?.name || null

  const title = isObligation ? 'This task is set by a compliance obligation' : 'This is a repeating task'
  const body = isObligation
    ? (obligationName
      ? `This task repeats on the schedule for the “${obligationName}” obligation. Apply this change to just this task, or to this and every future task created for this obligation?`
      : 'This task repeats on a compliance obligation’s schedule. Apply this change to just this task, or to this and every future task created for this obligation?')
    : 'Do you want this change to apply to just this task, or to this and all future tasks in the series as well?'
  const futureLabel = isObligation ? 'This & all future obligation tasks' : 'This & all future tasks'

  const promptNode = open
    ? createPortal(
      <Backdrop onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); choose(null) }}>
        <Card onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <Title>{title}</Title>
          <Body>{body}</Body>
          <Actions>
            <SecondaryBtn type="button" onClick={() => choose('this')}>This task only</SecondaryBtn>
            <PrimaryBtn type="button" onClick={() => choose('future')}>{futureLabel}</PrimaryBtn>
          </Actions>
        </Card>
      </Backdrop>,
      document.body
    )
    : null

  return { promptNode, requestScope }
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100050;
  padding: 20px;
`

const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 22px 22px 18px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
`

const Title = styled.h3`
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const Body = styled.p`
  margin: 0 0 18px;
  font-size: 14px;
  line-height: 1.5;
  color: #475569;
`

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
`

const SecondaryBtn = styled.button`
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 9px 16px;
  border-radius: 9px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #334155;
  cursor: pointer;
  min-height: 40px;

  &:hover { background: #f8fafc; }
`

const PrimaryBtn = styled.button`
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  padding: 9px 16px;
  border-radius: 9px;
  border: none;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  cursor: pointer;
  min-height: 40px;

  &:hover { filter: brightness(0.97); }
`
